"use client";

import { CreateGameDialog } from "@/app/oyun/_components/CreateGameDialog";
import { useNavbarPlay } from "@/app/_components/navbar/use-navbar-play";
import Link from "@/components/localized-link";
import { Avatar, AvatarFallback } from "@/components/shadcn/avatar";
import { cn } from "@/lib/utils";
import { Crown, Settings } from "lucide-react";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { APP_SHELL_NAV, type AppShellSection } from "./nav";

export interface AppShellUser {
    name: string;
    rating: number | null;
}

export function AppShellSidebar({
    active,
    user,
    onNavigate,
}: {
    active: AppShellSection;
    user: AppShellUser | null;
    onNavigate?: () => void;
}) {
    const t = useTranslations("nav");
    const rawPath = usePathname() ?? "";
    const path = rawPath.replace(/^\/az(?=\/|$)/, "") || "/";
    const { isCreateGameDialogOpen, setIsCreateGameDialogOpen, startPlaying } =
        useNavbarPlay(user !== null);

    return (
        <div className="flex h-full flex-col">
            <Link
                href="/"
                onClick={onNavigate}
                className="flex items-center gap-2 px-5 py-5 text-lg font-bold tracking-tight"
            >
                <span className="bg-primary grid size-8 place-items-center rounded-lg">
                    <Crown
                        className="text-primary-foreground size-5"
                        aria-hidden="true"
                    />
                </span>
                <span>
                    Satranç<span className="text-primary">365</span>
                </span>
            </Link>

            <nav
                className="flex flex-1 flex-col gap-1 px-3 py-2"
                aria-label={t("menu")}
            >
                {APP_SHELL_NAV.map((item) => {
                    const isActive =
                        item.section === active ||
                        (item.href === "/"
                            ? path === "/"
                            : path.startsWith(item.href));
                    const itemClassName = cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                        isActive
                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                            : "text-sidebar-foreground/75 hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground",
                    );

                    // "Oyna" has no plain destination — there's no bare /oyun
                    // page (only /oyun/[roomCode]), so it needs the same
                    // "go to your active game, or open the create-game
                    // dialog" logic the top navbar's own Oyna button uses,
                    // not a plain link (which 404s).
                    if (item.section === "play") {
                        return (
                            <button
                                key={item.section}
                                type="button"
                                onClick={() => {
                                    startPlaying();
                                    onNavigate?.();
                                }}
                                aria-current={isActive ? "page" : undefined}
                                className={itemClassName}
                            >
                                <item.icon
                                    className="size-4 shrink-0"
                                    aria-hidden="true"
                                />
                                {t(item.labelKey)}
                            </button>
                        );
                    }

                    return (
                        <Link
                            key={item.section}
                            href={item.href}
                            onClick={onNavigate}
                            aria-current={isActive ? "page" : undefined}
                            className={itemClassName}
                        >
                            <item.icon
                                className="size-4 shrink-0"
                                aria-hidden="true"
                            />
                            {t(item.labelKey)}
                        </Link>
                    );
                })}
            </nav>

            <div className="border-sidebar-border mt-auto flex flex-col gap-1 border-t px-3 py-3">
                <Link
                    href="/kullanici/ayarlar"
                    onClick={onNavigate}
                    className="text-sidebar-foreground/75 hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors"
                >
                    <Settings className="size-4 shrink-0" aria-hidden="true" />
                    {t("settings")}
                </Link>

                {user ? (
                    <Link
                        href="/kullanici"
                        onClick={onNavigate}
                        className="hover:bg-sidebar-accent/50 flex items-center gap-3 rounded-lg px-3 py-2 transition-colors"
                    >
                        <Avatar className="size-8">
                            <AvatarFallback>
                                {user.name.charAt(0).toUpperCase()}
                            </AvatarFallback>
                        </Avatar>
                        <span className="flex min-w-0 flex-col">
                            <span className="truncate text-sm font-medium">
                                {user.name}
                            </span>
                            {user.rating != null && (
                                <span className="text-muted-foreground text-xs">
                                    {user.rating} · Rapid
                                </span>
                            )}
                        </span>
                    </Link>
                ) : (
                    <Link
                        href="/login"
                        onClick={onNavigate}
                        className="text-primary px-3 py-2 text-sm font-medium hover:underline"
                    >
                        {t("login")}
                    </Link>
                )}
            </div>

            <CreateGameDialog
                open={isCreateGameDialogOpen}
                onOpenChange={setIsCreateGameDialogOpen}
            />
        </div>
    );
}
